/**
 * The training verdict's line on the coach page (#270): its reasons one tap
 * away, and the lighter session it offers.
 *
 * Two pieces, because they sit in two places. `VerdictWhy` is inside the
 * bubble, under the prose: a quiet "Why" that expands a recessed readout in
 * place, with "Hide" (PC-I40). `ProposalControls` is under the bubble, where
 * "Got it" sits for every other trigger: buttons below the coach's words,
 * never inside them (PC-I41). What each shows is decided in
 * `verdictPresentation.ts`, and the words come from the shared deck.
 */
import { useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";

import type { OpenerProposal, OpenerVerdict } from "../../api/types";
import { COACH_COPY } from "../../copy/coach";
import { EASE_OUT } from "../../lib/motion";
import { RECESSED } from "../builder/surfaces";
import { Eyebrow, TOKENS } from "../ui/dashboard-primitives";
import { AMBER } from "./tokens";
import type { ProposalAnswer } from "./types";
import { coverageLine, proposalView, showsGotIt, verdictEyebrow } from "./verdictPresentation";

/** A term and its sentence as one row of the readout's list, on the list's
 *  own two columns (a subgrid), so every sentence starts at one x. */
const ROW = { gridColumn: "1 / -1", gridTemplateColumns: "subgrid" } as const;

/** The quiet text controls' shared look: "Why", "Keep my plan", "Undo" and
 *  "Got it" read as one family, in #269's "Got it" look. Cream-soft over the
 *  page gradient: at 1280px wide, where the coach column crosses the tan
 *  band, they measured 3.48 to 4.39:1 against the lightest pixel behind
 *  them, short of AA. The fix belongs to the page, a scrim behind the coach
 *  column, not to the opener (#286).
 *  While an answer is in flight they dim, but only after 150ms: a quick
 *  answer would otherwise flash them for a frame. */
export const QUIET =
  "-ml-2 rounded-lg px-2 py-1.5 text-[13px] text-(--text-cream-soft) transition-[color,transform,opacity] duration-150 ease-out hover:text-(--text-cream) active:scale-[0.97] focus-visible:outline focus-visible:outline-2 focus-visible:outline-(--accent-amber-emphasis) disabled:opacity-60 disabled:delay-150";

export function VerdictWhy({ verdict }: { verdict: OpenerVerdict }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const history = coverageLine(verdict.coverage);
  // Reduced motion keeps the fade and drops the lift (DESIGN.md).
  const lift = useReducedMotion() ? 0 : -4;
  return (
    <div className="flex flex-col items-start gap-2">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
        className={QUIET}
        style={{ fontFamily: "var(--font-body)" }}
      >
        {open ? COACH_COPY.verdictHide : COACH_COPY.verdictWhy}
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={id}
            key="readout"
            initial={{ opacity: 0, y: lift }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: lift, transition: { duration: 0.14, ease: EASE_OUT } }}
            transition={{ duration: 0.2, ease: EASE_OUT }}
            className="@container flex w-full flex-col gap-2.5 rounded-xl px-4 py-3.5 wrap-anywhere"
            // `wrap-anywhere` (#310): a description can carry an event's title,
            // the user's own words (PC-I47), and so a word wider than the
            // bubble. The descriptions are grid items whose track width comes
            // from min-content, which only `anywhere` lowers (break-word
            // leaves it alone). It is inherited, so the headline, the action
            // line and every description take it from here; the labels keep
            // `whitespace-nowrap`.
            // The recessed readout (DESIGN.md): a thing you look into, the
            // builder's own muscle-summary panel, shared with iOS's
            // `Theme.Palette.recessed` byte for byte.
            style={{ background: RECESSED.panel }}
          >
            <Eyebrow>{verdictEyebrow(verdict.verdict)}</Eyebrow>
            {verdict.headline && (
              <p className="m-0 text-[14px] leading-snug" style={{ color: TOKENS.cream }}>
                {verdict.headline}
              </p>
            )}
            {verdict.action_text && (
              <p className="m-0 text-[13px] leading-relaxed" style={{ color: TOKENS.creamSoft }}>
                {verdict.action_text}
              </p>
            )}
            {(verdict.reasons.length > 0 || history) && (
              // One grid for every row, so the sentences line up in one
              // column whatever each label's length; a label and its sentence
              // are a term and its description. Each pair is a subgrid row
              // of the list's own columns, so a row can carry a hairline of
              // its own: on the two baseline-aligned cells it stepped, the
              // label's cell starting lower than its sentence's. On a phone
              // the readout is too narrow for two columns (a sentence beside
              // RUNNING ON EMPTY got about 90px), so each label stands over
              // its sentence, as the iOS sheet does.
              <dl className="m-0 grid grid-cols-1 gap-y-1.5 @sm:grid-cols-[minmax(0,max-content)_1fr]">
                {verdict.reasons.map((r, i) => (
                  <div key={`${r.label}-${i}`} className="grid items-baseline gap-y-0.5" style={ROW}>
                    {/* Cream, never terracotta: the verdict is a suggestion,
                        and the warning colour beside a proposal reads as an
                        alarm (PC-I43). */}
                    <dt
                      className="whitespace-nowrap text-[0.6875rem] uppercase tracking-[0.1em]"
                      style={{ fontFamily: "var(--font-mono)", color: TOKENS.creamDim }}
                    >
                      {r.label}
                    </dt>
                    {/* Plain text, always: an event's title is the user's
                        own words (PC-I47). A blank one leaves this empty,
                        which keeps the pair whole for a screen reader. */}
                    <dd className="m-0 text-sm @sm:pl-[18px]" style={{ color: TOKENS.creamSoft }}>
                      {r.detail}
                    </dd>
                  </div>
                ))}
                {history && (
                  // The web Standing ledger's own History row, same tokens
                  // (D-245-2, PC-I43): amber is a limit of the measurement,
                  // not a warning about the person, so never terracotta. Its
                  // hairline is the row's, as the ledger's is its paragraph's.
                  <div
                    className="grid items-baseline gap-y-0.5"
                    style={{
                      ...ROW,
                      ...(verdict.reasons.length
                        ? { borderTop: "1px solid " + TOKENS.creamHairline, marginTop: 4, paddingTop: 10 }
                        : {}),
                    }}
                  >
                    <dt
                      className="whitespace-nowrap text-[0.6875rem] uppercase tracking-[0.1em]"
                      style={{ fontFamily: "var(--font-mono)", color: AMBER }}
                    >
                      {COACH_COPY.verdictHistoryLabel}
                    </dt>
                    <dd className="m-0 text-sm @sm:pl-[18px]" style={{ color: TOKENS.creamSoft }}>
                      {history}
                    </dd>
                  </div>
                )}
              </dl>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function ProposalControls({
  proposal,
  onAnswer,
  onDismiss,
}: {
  proposal: OpenerProposal | null | undefined;
  onAnswer: (answer: ProposalAnswer) => Promise<unknown>;
  onDismiss: () => Promise<unknown> | void;
}) {
  const [busy, setBusy] = useState(false);
  // The last answer or dismissal was not saved (PC-I54).
  const [failed, setFailed] = useState(false);
  const view = proposalView(proposal);
  const box = useRef<HTMLDivElement>(null);
  // Whether the keyboard was on one of these controls when an answer went
  // out. The control it was on is replaced when the answer lands (the offer
  // by its record line and Undo, and back), and focus would fall to the page.
  const hadFocus = useRef(false);

  useEffect(() => {
    const root = box.current;
    if (busy || !hadFocus.current || !root) return;
    hadFocus.current = false;
    if (!root.contains(document.activeElement)) {
      root.querySelector<HTMLButtonElement>("button")?.focus();
    }
  }, [view.kind, busy]);

  // Every control waits while a request is in flight; a failed one leaves
  // them where they were, with no record line claiming what did not happen,
  // and a note under them that says so (PC-I54). Trying again clears it.
  const run = async (act: () => Promise<unknown> | void) => {
    hadFocus.current = box.current?.contains(document.activeElement) ?? false;
    setFailed(false);
    setBusy(true);
    try {
      if ((await act()) === false) setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const failure = failed && <OpenerWriteFailed />;

  const gotIt = showsGotIt(view) && (
    <button
      type="button"
      disabled={busy}
      onClick={() => run(onDismiss)}
      className={QUIET}
      style={{ fontFamily: "var(--font-body)" }}
    >
      {COACH_COPY.openerGotIt}
    </button>
  );

  if (view.kind === "none") {
    return (
      <div ref={box} className="flex flex-col items-start gap-1">
        <div className="flex items-center">{gotIt}</div>
        {failure}
      </div>
    );
  }

  if (view.kind === "answered") {
    // The record line above its controls, the offer's own shape (PC-I57).
    return (
      <div ref={box} className="flex flex-col items-start gap-1">
        <p role="status" className="m-0 text-[13px]" style={{ color: TOKENS.creamSoft }}>
          {view.record}
        </p>
        {(view.undo || gotIt) && (
          <div className="flex flex-wrap items-center gap-x-1">
            {view.undo && (
              <button
                type="button"
                disabled={busy}
                onClick={() => run(() => onAnswer("withdrawn"))}
                className={QUIET}
                style={{ fontFamily: "var(--font-body)" }}
              >
                {COACH_COPY.proposalUndo}
              </button>
            )}
            {gotIt}
          </div>
        )}
        {failure}
      </div>
    );
  }

  return (
    <div ref={box} className="flex flex-col items-start gap-1.5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        {view.quiet ? (
          // Under rest the lighter session is offered second (PC-I27): quiet
          // text, so the visual weight follows the advice (PC-I59).
          <button
            type="button"
            data-emphasis="quiet"
            disabled={busy}
            onClick={() => run(() => onAnswer("accepted"))}
            className={QUIET}
            style={{ fontFamily: "var(--font-body)" }}
          >
            {view.accept}
          </button>
        ) : (
        <button
          type="button"
          data-emphasis="primary"
          disabled={busy}
          onClick={() => run(() => onAnswer("accepted"))}
          className="inline-flex min-h-9 items-center rounded-full border border-(--accent-amber-glow) px-3.5 py-1.5 text-[13px] text-(--accent-amber) transition-[color,border-color,transform,opacity] duration-150 ease-out hover:border-(--accent-amber-emphasis) hover:text-(--accent-amber-light) active:scale-[0.97] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--accent-amber-emphasis) disabled:opacity-60 disabled:delay-150"
          // On the builder's recessed well, not the amber tint iOS uses:
          // the web's pill sits over the gradient's mauve-tan band, where
          // amber on the tint measured 3.37:1 at its lightest (PC-I53), and
          // amber-light text only 3.87. The well gives 6.23.
          style={{ background: RECESSED.panel, fontFamily: "var(--font-body)" }}
        >
          {view.accept}
        </button>
        )}
        {view.decline && (
          <button
            type="button"
            disabled={busy}
            onClick={() => run(() => onAnswer("declined"))}
            className={QUIET}
            style={{ fontFamily: "var(--font-body)" }}
          >
            {view.decline}
          </button>
        )}
        {gotIt}
      </div>
      <p className="m-0 text-[12px]" style={{ color: TOKENS.creamDim }}>
        {view.scope}
      </p>
      {failure}
    </div>
  );
}

/**
 * "Couldn't save that. Try again." under the opener's controls when an answer
 * or a dismissal was not saved and trying again can work (PC-I54, PC-I61).
 * The controls stay as they were, so trying again is one click. An alert,
 * so a screen reader says it.
 *
 * On the house error note's surface, PanelError's `--warm-dark` fill with the
 * `--terracotta-soft` wash (PC-I63). Where this note sits on the coach page,
 * over the gradient's tan band at 1280px wide, terracotta-text measured
 * 2.42:1 on the bare gradient and 4.86:1 on the surface. `-ml-2.5` puts its
 * text on the column's edge, where the quiet controls' text sits.
 */
export function OpenerWriteFailed() {
  return (
    <p
      role="alert"
      className="m-0 -ml-2.5 rounded-lg px-2.5 py-1.5 text-[12px]"
      style={{
        color: TOKENS.terracottaText,
        backgroundColor: TOKENS.warmDark,
        backgroundImage: `linear-gradient(${TOKENS.terracottaSoft}, ${TOKENS.terracottaSoft})`,
      }}
    >
      {COACH_COPY.openerWriteFailed}
    </p>
  );
}
